import { useState } from "react";

import type {
  SlotMetrics,
  VideoLatencyMode,
  VideoComparisonSummary,
} from "../modules/teleop-session";
import { videoComparisonCsv } from "../modules/teleop-session/media/video-comparison-report";
import styles from "./VideoLatencyPanel.module.scss";

export interface VideoLatencyActions {
  setLatencyMode?: (slot: number, mode: VideoLatencyMode) => void;
  startLatencyComparison?: (slot: number) => boolean;
  cancelLatencyComparison?: (slot: number) => void;
}

const modeLabels = { normal: "Обычный", minimum: "Минимальная задержка" };
const statusLabels = {
  complete: "Сравнение завершено",
  cancelled: "Сравнение остановлено",
  failed: "Сравнение прервано",
};

function number(value: number | null, unit = "", digits = 1) {
  return value === null ? "—" : `${value.toFixed(digits)}${unit}`;
}

const rows: { label: string; value: (s: VideoComparisonSummary) => string }[] = [
  { label: "Буфер, среднее", value: (s) => number(s.jitterMeanMs, " мс") },
  { label: "Буфер, P95", value: (s) => number(s.jitterP95Ms, " мс") },
  { label: "Декодирование", value: (s) => number(s.decodeMeanMs, " мс") },
  { label: "FPS декодирования", value: (s) => number(s.fpsMean) },
  { label: "Пропущено кадров", value: (s) => number(s.droppedFrames, "", 0) },
  { label: "Интервалы без кадров", value: (s) => number(s.noFramesSeconds, " с") },
  { label: "Время измерения", value: (s) => number(s.measuredSeconds, " с") },
];

export function VideoLatencyPanel({
  slot,
  camera,
  metrics,
  setLatencyMode,
  startLatencyComparison,
  cancelLatencyComparison,
}: {
  slot: number;
  camera: string;
  metrics: SlotMetrics | undefined;
} & VideoLatencyActions) {
  const [startError, setStartError] = useState(false);
  if (!setLatencyMode) return null;
  const comparison = metrics?.comparison;
  const running = comparison?.status === "running";
  const ready = metrics?.latencySupport === "ready";
  const supportText =
    metrics?.latencySupport === "unsupported"
      ? "Этот браузер не поддерживает настройку буфера."
      : metrics?.latencySupport === "error"
        ? "Браузер не применил настройку буфера."
        : !ready
          ? "Настройка станет доступна после подключения потока."
          : null;

  function download() {
    if (!comparison) return;
    const url = URL.createObjectURL(
      new Blob(["\uFEFF", videoComparisonCsv(camera, slot, comparison)], {
        type: "text/csv;charset=utf-8",
      })
    );
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `video-latency-slot-${slot + 1}-${comparison.startedAtIso.replace(/[:.]/g, "-")}.csv`;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  return (
    <div className={styles.panel}>
      <label className={styles.mode}>
        <span>Режим задержки</span>
        <select
          aria-label={`${camera}: режим задержки`}
          value={metrics?.latencyMode ?? "normal"}
          disabled={running || (!ready && metrics?.latencySupport !== "error")}
          onChange={(e) => {
            setStartError(false);
            setLatencyMode?.(slot, e.target.value as VideoLatencyMode);
          }}
        >
          <option value="normal">{modeLabels.normal}</option>
          <option value="minimum">{modeLabels.minimum}</option>
        </select>
      </label>
      <p className={styles.note}>
        {supportText ??
          "Минимальный режим запрашивает буфер 0 мс; фактическое время определяет браузер. При нестабильной сети возможны пропуски кадров."}
      </p>
      <div className={styles.actions}>
        {running ? (
          <button type="button" onClick={() => cancelLatencyComparison?.(slot)}>
            Остановить сравнение
          </button>
        ) : (
          <button
            type="button"
            disabled={!ready || !startLatencyComparison}
            onClick={() => setStartError(!startLatencyComparison?.(slot))}
          >
            Сравнить за 2 минуты
          </button>
        )}
        {comparison && !running && (
          <button type="button" onClick={download}>
            Скачать CSV
          </button>
        )}
      </div>
      {startError && <p role="status">Не удалось начать: дождитесь подключения потока.</p>}
      {comparison && (
        <>
          <p className={styles.progress} role="status">
            {running
              ? `${modeLabels[comparison.phase]} · осталось ${comparison.remainingSeconds} с`
              : `${statusLabels[comparison.status as keyof typeof statusLabels]}${comparison.reason ? `: ${comparison.reason}` : ""}. Выбранный режим восстановлен.`}
          </p>
          <table aria-label={`${camera}: сравнение задержки`}>
            <thead>
              <tr>
                <th>Показатель</th>
                <th>Обычный</th>
                <th>Минимальный</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label}>
                  <th scope="row">{row.label}</th>
                  <td>{row.value(comparison.normal)}</td>
                  <td>{row.value(comparison.minimum)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      <p className={styles.note}>
        По минуте на режим; первые 5 с не учитываются. P95 — по средним за секундные интервалы.
        Короткие остановки меньше интервала замера здесь не видны. После теста вернётся выбранный
        режим.
      </p>
    </div>
  );
}
